import { useStripeCustomerPortal } from '@/hooks/billing/useStripeCustomerPortal';
import type { ComponentProps } from 'react';
type UpdatePaymentButtonProps = Omit<ComponentProps<'button'>, 'type'>;

/**
 * Sends a user with an overdue payment to the Stripe billing portal.
 *
 * Replaces the `/payments/[uuid]` page, which existed only to mint a portal
 * session and redirect. Stripe mints that URL per click, so this has to be a
 * button rather than a link — and the redirect lands in the same tab, where
 * the old links opened a new one.
 *
 * `onClick` has to be called through rather than omitted: several call sites
 * nest this in `<Button asChild onClick={dismissPopup}>`, and Radix's Slot
 * merges that handler into this component's props rather than binding it to
 * the DOM node itself. Overwriting it would silently drop the dismiss.
 */
export const UpdatePaymentButton = ({
  children,
  disabled,
  onClick,
  ...props
}: UpdatePaymentButtonProps) => {
  const {
    openCustomerPortal,
    isLoading
  } = useStripeCustomerPortal();
  return <button {...props} disabled={disabled || isLoading} onClick={event => {
    onClick?.(event);
    openCustomerPortal();
  }} type="button" data-sentry-component="UpdatePaymentButton" data-sentry-source-file="index.tsx">
      {children}
    </button>;
};